import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import Login from './components/login';
import Logout from './components/logout';
import LoadCases from './components/loadcases';
import LoadQuotes from './components/loadquotes';
import LoadQuote from './components/loadquote';
import Quotes from './components/quotes';
import Quote from './components/quote';
import Hub from './components/hub';
import Home from './components/home';
import LoadCase from './components/loadcase';
import Case from './components/case';
import LinkedCase from './components/linkedcase';
import Actions from './components/actions';
import Instruct from './components/instruct';
import WishList from './components/wishlist';
import Analytics from './components/analytics';
import Teams from './components/teams';
import Team from './components/team';
import LinkedTeam from './components/linkedteam';
import { useAppDispatch, useAppSelector } from './slices/index';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faFilePen, faGear, faSync, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/free-solid-svg-icons';
import * as UI from './slices/uiSlice';
import * as Cases from './slices/casesSlice';
import * as User from './slices/userSlice';
import * as Forms from './slices/formsSlice';
import { AnyAction } from '@reduxjs/toolkit';
import { doFormOnChange, getFormValidWithId, getFormValueWithId, getJSONWithFormValues, validateForm } from './utilities';
import LinkedActions from './components/linkedactions';
import HistoryList from './components/historylist';
import EditTrackerChart from './components/editTrackerChart';
import CaseDetails from './components/caseDetails';
import SuccessIcon from './assets/success-icon.svg';
import CloseIcon from './assets/close-icon.svg';
import ProtectedRoute from './components/protectedRoute';

export function getTitle(path: string): string {
	const titleMap = [
		{ path: '/', title: '' },
		{ path: '/loadcases', title: 'Load Cases' },
		{ path: '/loadcase', title: 'Load Case' },
		{ path: '/loadquotes', title: 'Load Quotes' },
		{ path: '/loadquote', title: 'Load Quote' },
		{ path: '/quotes', title: 'List Quotes' },
		{ path: '/quote', title: 'View Quote' },
		{ path: '/hub', title: 'Main Hub' },
		{ path: '/home', title: 'Home' },
		{ path: '/instruct', title: 'Instruct PLS' },
		{ path: '/wishlist', title: 'Your Wishlist' },
		{ path: '/analytics', title: 'Analytics' },
		{ path: '/teams', title: 'Meet Your Teams' },
		{ path: '/team', title: 'Meet Your Team' },
		{ path: '/linkedteam', title: 'Meet the Team' },
		{ path: '/case', title: 'View Case' },
		{ path: '/case-details', title: 'Case Details' },
		{ path: '/linkedcase', title: 'Linked Case' },
		{ path: '/actions', title: 'Actions' },
		{ path: '/linkedactions', title: 'Linked Actions' },
		{ path: '/historylist', title: 'History' },
		{ path: '/tracker-chart', title: 'Tracker Chart' },
		{ path: '/logout', title: '' }
	];
	const curTitle = (titleMap.find(item => item.path === path) as any);
	return (curTitle ? curTitle.title : '');
}

function App() {
	const dispatch = useAppDispatch();
	const ui = useAppSelector(state => state.ui);
	const user = useAppSelector(state => state.user);
	const cases = useAppSelector(state => state.cases);
	const forms = useAppSelector(state => state.forms);

	const disableMonth = (m: number): boolean => {
		const d = new Date();
		const month = d.getMonth();
		for (let i = month + 1; i < month + 10; i++) {
			let nym = i % 12;
			if (m === nym) {
				return false;
			}
		}
		return true;
	}

	const closePopup = () => {
		dispatch(UI.setInfo({ visible: false, message: '' }));
		dispatch(UI.setError({ visible: false, message: '' }));
		dispatch(UI.setWishlistPopup({ visible: false, caseno: '', casetype: '' }));
		dispatch(UI.setWishlistEditPopup({ visible: false, caseno: '', casetype: '' }));
	}

	const onClick = () => {
		if (ui.menuLeft === 415) {
			dispatch((UI.closeMenu()) as unknown as AnyAction);
		}
	};

	const removeWishlist = () => {
		dispatch(Cases.setWishlisting(true));

		const state: Cases.removeWishlistState = {
			caseno: ui.wishlistPopupCaseno,
			id: user.id,
			token: user.token,
		}

		dispatch(Cases.removeWishlist(state) as unknown as AnyAction).then(() => {
			dispatch(UI.setWishlistEditPopup({ visible: false, caseno: '', casetype: '' }));
			dispatch(Cases.searchCases({ searchStr: cases.searchStr, isWishlisted: false }));
		}
		);
	}

	const updateWishlist = () => {
		dispatch(UI.setWishlistPopup({ visible: true, caseno: ui.wishlistPopupCaseno, casetype: ui.wishlistPopupCasetype }));
		dispatch(UI.setWishlistEditPopup({ visible: false, caseno: ui.wishlistPopupCaseno, casetype: ui.wishlistPopupCasetype }));
	}

	const wishlistCase = () => {
		if (validateForm('WISHLIST', 'WISHLIST')) {
			dispatch(Cases.setWishlisting(true));
			const json = getJSONWithFormValues('WISHLIST');
			const data = JSON.parse(json);

			const state: Cases.wishlistCaseState = {
				caseno: ui.wishlistPopupCaseno,
				id: user.id,
				usertype: user.type,
				token: user.token,
				casetype: cases.case.casetype,
				type: data.wishlist_type.val,
				month: data.wishlist_month.val,
			}

			dispatch(Cases.wishlistCase(state) as unknown as AnyAction).then(() => {
				dispatch(UI.setWishlistPopup({ visible: false, caseno: '', casetype: '' }));
				dispatch(Forms.removeFormWithId('WISHLIST'));
				dispatch(Cases.searchCases({ searchStr: cases.searchStr, isWishlisted: false }));
			}
			);
		}
	}

	const changePassword = () => {
		let passError: string = '';
		if (ui.newPassword === '' || ui.confirmPassword === '') {
			passError = passError.concat("PLEASE ENTER AND CONFIRM PASSWORD\n");
		}
		if (ui.newPassword !== ui.confirmPassword) {
			passError = passError.concat("PASSWORDS DO NOT MATCH\n");
		}
		if (ui.newPassword.length < 8) {
			passError = passError.concat("PLEASE USE AT LEAST 8 CHARACTERS\n");
		}
		if (/\d/.test(ui.newPassword) == false) {
			passError = passError.concat("PLEASE USE AT LEAST 1 NUMBER\n");
		}
		if (/[a-z]/.test(ui.newPassword) == false) {
			passError = passError.concat("PLEASE USE AT LEAST 1 LOWERCASE LETTER\n");
		}
		if (/[A-Z]/.test(ui.newPassword) == false) {
			passError = passError.concat("PLEASE USE AT LEAST 1 UPPERCASE LETTER\n");
		}
		dispatch(UI.setPassError(passError));
		if (passError !== '') {
			return;
		}
		dispatch(UI.setChangingPass(true));
		dispatch(User.userChangePass({ caseno: user.id, pass: ui.newPassword, session: user.token }) as unknown as AnyAction).then(() => {
			dispatch(UI.setChangingPass(false));
			dispatch(UI.setChangePasswordPopup(false));
			dispatch(UI.setInfo({ visible: true, message: 'Your password has been changed.' }));
		}
		);
	}

	return (
		<div className="App" onClick={onClick}>
			{ui.changePasswordPopupVisible && <div className='container'>
				<div className='popup'>
					<div style={{ display: 'flex' }}>
						<FontAwesomeIcon style={{ fontSize: 24, marginRight: 8 }} icon={faCircleInfo} />
						<div>Change Password</div>
					</div>
					<div>You have logged in with a temporary password.</div>
					<div style={{ fontFamily: 'OpenSans-Bold', color: 'red' }}>You MUST now set a new password.</div>
					<div style={{ textAlign: 'center' }}>Please use at least 8 characters including at least 1 lowercase letter, 1 uppercase letter and 1 number</div>
					<div style={{ fontFamily: 'OpenSans-Bold' }}>New Password</div>
					<input type="password" value={ui.newPassword} onChange={e => dispatch(UI.setNewPassword(e.target.value))} />
					<div style={{ fontFamily: 'OpenSans-Bold' }}>Confirm Password</div>
					<input type="password" value={ui.confirmPassword} onChange={e => dispatch(UI.setConfirmPassword(e.target.value))} />
					<div style={{ textAlign: 'center', whiteSpace: 'pre-line' }}>{ui.passError}</div>
					<div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
						<div className='buttonGreen' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: 128, height: 21 }} onClick={() => changePassword()}>
							<div style={{ display: 'flex' }}>
								<FontAwesomeIcon style={{ fontSize: 18, marginRight: 8 }} icon={faGear} />
								<div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Change</div>
							</div>
							{ui.isChangingPass &&
								<FontAwesomeIcon className='fa-spin' style={{ fontSize: 18, marginRight: 8 }} icon={faSync} />
							}
						</div>
					</div>
				</div>
			</div>}
			{ui.infoVisible && <div className='container'>
				<div className='popup'>
					<div style={{ display: 'flex' }}>
						<FontAwesomeIcon style={{ fontSize: 24, marginRight: 8 }} icon={faCircleInfo} />
						<div>{ui.infoMessage}</div>
					</div>
					<div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
						<div className='buttonGreen' style={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flex: '0 0 128px', height: 21 }} onClick={() => closePopup()}>
							<div style={{ display: 'flex' }}>
								<FontAwesomeIcon style={{ fontSize: 18, marginRight: 8 }} icon={faCircleXmark} />
								<div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Close</div>
							</div>
						</div>
					</div>
				</div>
			</div>}
			{ui.errorVisible && <div className='container'>
				<div className='popup'>
					<div style={{ display: 'flex' }}>
						<FontAwesomeIcon style={{ fontSize: 24, marginRight: 8, color: 'red' }} icon={faCircleExclamation} />
						<div>{ui.errorMessage}</div>
					</div>
					<div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
						<div className='buttonGreen' style={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flex: '0 0 128px', height: 21 }} onClick={() => closePopup()}>
							<div style={{ display: 'flex' }}>
								<FontAwesomeIcon style={{ fontSize: 18, marginRight: 8 }} icon={faCircleXmark} />
								<div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Close</div>
							</div>
						</div>
					</div>
				</div>
			</div>}
			{ui.wishlistPopupVisible && <div className='container'>
				<div className='popup'>
					<div id='WISHLIST' style={{ display: 'flex', flexDirection: 'column' }}>
						<div style={{ fontFamily: 'OpenSans-Bold', fontSize: 28 }}>Wishlist: {ui.wishlistPopupCaseno}</div>
						<div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
							<div style={{ flex: '1 1 0px', margin: 4 }}>Action:</div>
							<div style={{ flex: '1 1 0px', margin: 4 }}>
								<select data-form-id={'WISHLIST'} data-valid-type="*" className={getFormValidWithId('WISHLIST', "wishlist_type") ? undefined : 'error'} value={getFormValueWithId('WISHLIST', "wishlist_type")} name='wishlist_type' id='wishlist_type' onChange={(e) => doFormOnChange(e)}>
									<option value=''>Please Select...</option>
									<option disabled={ui.wishlistPopupCasetype === 'Remortgage'} value='EXCH'>Exchange</option>
									<option value='COMP'>Complete</option>
								</select>
							</div>
						</div>
						<div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
							<div style={{ flex: '1 1 0px', margin: 4 }}>Month:</div>
							<div style={{ flex: '1 1 0px', margin: 4 }}>
								<select data-form-id={'WISHLIST'} data-valid-type="*" className={getFormValidWithId('WISHLIST', "wishlist_month") ? undefined : 'error'} value={getFormValueWithId('WISHLIST', "wishlist_month")} name='wishlist_month' id='wishlist_month' onChange={(e) => doFormOnChange(e)}>
									<option value=''>Please Select...</option>
									<option value='0' disabled={disableMonth(0)}>January</option>
									<option value='1' disabled={disableMonth(1)}>February</option>
									<option value='2' disabled={disableMonth(2)}>March</option>
									<option value='3' disabled={disableMonth(3)}>April</option>
									<option value='4' disabled={disableMonth(4)}>May</option>
									<option value='5' disabled={disableMonth(5)}>June</option>
									<option value='6' disabled={disableMonth(6)}>July</option>
									<option value='7' disabled={disableMonth(7)}>August</option>
									<option value='8' disabled={disableMonth(8)}>September</option>
									<option value='9' disabled={disableMonth(9)}>October</option>
									<option value='10' disabled={disableMonth(10)}>November</option>
									<option value='11' disabled={disableMonth(11)}>December</option>
								</select>
							</div>
						</div>
						<div>
							<div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
								<div className='buttonGreen' style={{ display: 'flex', alignItems: 'center', width: 128, height: 21 }} onClick={() => closePopup()}>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										<FontAwesomeIcon style={{ fontSize: 18, marginRight: 8 }} icon={faCircleXmark} />
										<div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Close</div>
									</div>
								</div>
								<div className='buttonGreen' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: 128, height: 21 }} onClick={() => wishlistCase()}>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										<FontAwesomeIcon style={{ fontSize: 18, marginRight: 8 }} icon={faHeartSolid} />
										<div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Wishlist</div>
									</div>
									{cases.isWishlisting &&
										<FontAwesomeIcon className='fa-spin' style={{ fontSize: 18, marginRight: 8 }} icon={faSync} />
									}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>}
			{ui.wishlistEditPopupVisible && <div className='container'>
				<div className='popup'>
					<div id='WISHLIST' style={{ display: 'flex', flexDirection: 'column' }}>
						<div style={{ fontFamily: 'OpenSans-Bold', fontSize: 28 }}>Wishlist: {ui.wishlistPopupCaseno}</div>
						<div>Would you like to Remove or Update the Wishlist on this Case?</div>
						<div>
							<div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
								<div className='buttonGreen' style={{ display: 'flex', alignItems: 'center', width: 128, height: 21 }} onClick={() => closePopup()}>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										<FontAwesomeIcon style={{ fontSize: 18, marginRight: 8 }} icon={faCircleXmark} />
										<div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Close</div>
									</div>
								</div>
								<div className='buttonGreen' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: 128, height: 21 }} onClick={() => removeWishlist()}>
									<div style={{ display: 'flex' }}>
										<FontAwesomeIcon style={{ fontSize: 18, marginRight: 8 }} icon={faTrashCan} />
										<div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Remove</div>
									</div>
									{cases.isWishlisting &&
										<FontAwesomeIcon className='fa-spin' style={{ fontSize: 18, marginRight: 8 }} icon={faSync} />
									}
								</div>
								<div className='buttonGreen' style={{ display: 'flex', alignItems: 'center', width: 128, height: 21 }} onClick={() => updateWishlist()}>
									<div style={{ display: 'flex' }}>
										<FontAwesomeIcon style={{ fontSize: 18, marginRight: 8 }} icon={faFilePen} />
										<div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Update</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>}
			{ui.isEmailSent && <div className='emailSentContainerWrapper'>
				<div className='emailSentContainer'>
					<img src={CloseIcon} alt="Close-Icon" className="emailSentContainerCloseIcon" onClick={() => dispatch(UI.setEmailSent(false))} />
					<img src={SuccessIcon} alt="Success-Icon" className="emailSentContainerIcon" />
					<p className="emailSentContainerText">Email Sent Successfully!</p>
				</div>
			</div>}

			<BrowserRouter basename="/">
                <Routes>
                    <Route path="/" element={<Login />} />
                    <Route path="/logout" element={<Logout />} />
                    {/* All Protected Routes */}
                    <Route path="/loadcases" element={<ProtectedRoute><LoadCases /></ProtectedRoute>} />
                    <Route path="/loadquotes" element={<ProtectedRoute><LoadQuotes /></ProtectedRoute>} />
                    <Route path="/loadquote" element={<ProtectedRoute><LoadQuote /></ProtectedRoute>} />
                    <Route path="/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
                    <Route path="/quote" element={<ProtectedRoute><Quote /></ProtectedRoute>} />
                    <Route path="/hub" element={<ProtectedRoute><Hub /></ProtectedRoute>} />
                    <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                    <Route path="/instruct" element={<ProtectedRoute><Instruct /></ProtectedRoute>} />
                    <Route path="/wishlist" element={<ProtectedRoute><WishList /></ProtectedRoute>} />
                    <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
                    <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
                    <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
                    <Route path="/linkedteam" element={<ProtectedRoute><LinkedTeam /></ProtectedRoute>} />
                    <Route path="/loadcase" element={<ProtectedRoute><LoadCase /></ProtectedRoute>} />
                    <Route path="/case" element={<ProtectedRoute><Case /></ProtectedRoute>} />
                    <Route path="/case-details" element={<ProtectedRoute><CaseDetails /></ProtectedRoute>} />
                    <Route path="/linkedcase" element={<ProtectedRoute><LinkedCase /></ProtectedRoute>} />
                    <Route path="/actions" element={<ProtectedRoute><Actions /></ProtectedRoute>} />
                    <Route path="/linkedactions" element={<ProtectedRoute><LinkedActions /></ProtectedRoute>} />
                    <Route path="/historylist" element={<ProtectedRoute><HistoryList /></ProtectedRoute>} />
                    <Route path="/tracker-chart" element={<ProtectedRoute><EditTrackerChart /></ProtectedRoute>} />
                </Routes>
            </BrowserRouter>
 
		</div>
	);
}

export default App;
